/**
 * 取引・支払い状況・取引ステータスの型と入力検証（02 5.3.7 transaction）。
 * - 支払い状況（payment_status）はサーバーが請求日・入金日から決める（要件書 2.3）。本文では受け付けない
 * - 請求日・入金日を消す（null にする）変更は API-TRAN-08（管理者・理由必須）だけ。PATCH で消すと 409 STATE_001
 * - 金額は利用者の入力を正とし、数量×単価で上書きしない（D-042）
 */
import { z } from 'zod';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { PAYMENT_STATUS_VALUES } from '../enums/payment-status';
import { zCode, zDate, zMoney, zRequiredText, zShortText, zSystemCode, zVersion } from './common.schema';
import { zNullable } from './customer.schema';
import { SALES_REFERENCE_SHAPE } from './case.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 数量（整数 9 桁・小数 2 桁：D-041） */
export const zQuantity = z.string({ message: 'MSG-V-005' }).regex(/^\d{1,9}(\.\d{1,2})?$/, 'MSG-V-005');

const TRANSACTION_INPUT_SHAPE = {
  transaction_name: zRequiredText(200, 'MSG-V-068'),
  ...SALES_REFERENCE_SHAPE,
  case_code: zNullable(zCode).optional(),
  transaction_status: zCode.optional(),
  transaction_date: z.string({ message: 'MSG-V-067' }).min(1, 'MSG-V-067').pipe(zDate),
  transaction_content: zShortText(5000).optional(),
  next_action_date: zNullable(zDate).optional(),
  product_name: zShortText(200).optional(),
  product_type: zShortText(100).optional(),
  quantity: zNullable(zQuantity).optional(),
  unit_price: zNullable(zMoney).optional(),
  amount: z.string({ message: 'MSG-V-054' }).min(1, 'MSG-V-054').pipe(zMoney),
  supplier_name: zShortText(200).optional(),
  /** 274：商品の備考（旧 product_remarks。商品情報の「備考」） */
  product_remarks: zShortText(1000).optional(),
  payment_terms: zShortText(200).optional(),
  invoice_date: zNullable(zDate).optional(),
  payment_due_date: zNullable(zDate).optional(),
  paid_date: zNullable(zDate).optional(),
  lost_reason: zShortText(1000).optional(),
};

/** API-TRAN-02 取引の登録 */
export const TransactionCreateSchema = z.object(TRANSACTION_INPUT_SHAPE).strict();

/** API-TRAN-04 取引の更新（すべて任意＋version） */
export const TransactionUpdateSchema = z
  .object({
    ...TRANSACTION_INPUT_SHAPE,
    transaction_name: TRANSACTION_INPUT_SHAPE.transaction_name.optional(),
    customer_code: TRANSACTION_INPUT_SHAPE.customer_code.optional(),
    transaction_date: TRANSACTION_INPUT_SHAPE.transaction_date.optional(),
    amount: TRANSACTION_INPUT_SHAPE.amount.optional(),
    version: zVersion,
  })
  .strict();

/** API-TRAN-08 支払い状況を戻す（戻す先・理由・version） */
export const PaymentStatusReversionSchema = z
  .object({
    target_payment_status: z.enum(['unbilled', 'invoiced'], { message: 'MSG-V-020' }),
    reason: zRequiredText(500),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const TransactionSchema = z
  .object({
    transaction_code: z.string(),
    transaction_name: z.string(),
    customer_code: z.string(),
    customer_name: z.string().nullable(),
    customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    customer_contact_code: z.string().nullable(),
    customer_contact_name: z.string().nullable(),
    customer_contact_department: z.string().nullable(),
    case_code: z.string().nullable(),
    case_name: z.string().nullable(),
    /** 案件が削除済み（02 2.9「<参照先>_deleted」） */
    case_deleted: z.boolean(),
    sales_user_code: z.string(),
    sales_user_name: z.string().nullable(),
    transaction_status: z.string(),
    transaction_status_name: z.string().nullable(),
    transaction_status_updated_at: z.string(),
    transaction_date: z.string(),
    transaction_content: z.string().nullable(),
    next_action_date: z.string().nullable(),
    product_name: z.string().nullable(),
    product_type: z.string().nullable(),
    quantity: z.string().nullable(),
    unit_price: z.string().nullable(),
    amount: z.string(),
    supplier_name: z.string().nullable(),
    product_remarks: z.string().nullable(),
    payment_terms: z.string().nullable(),
    invoice_date: z.string().nullable(),
    payment_due_date: z.string().nullable(),
    paid_date: z.string().nullable(),
    payment_status: z.enum(PAYMENT_STATUS_VALUES),
    lost_reason: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const TransactionStatusSchema = z.object({
  transaction_status: z.string(),
  transaction_status_name: z.string(),
  display_order: z.number().int(),
});

export const TRANSACTION_LIST_SORT_VALUES = ['transaction_code', 'transaction_date', 'amount', 'created_at'] as const;

/** API-TRAN-01（02 2.3.2） */
export const TransactionListQuerySchema = buildListQuerySchema(TRANSACTION_LIST_SORT_VALUES, '-created_at').extend({
  transaction_status: zCode.optional(),
  payment_status: z.enum(PAYMENT_STATUS_VALUES, { message: 'MSG-V-020' }).optional(),
  customer_code: zCode.optional(),
  case_code: zCode.optional(),
  sales_user_code: zSystemCode.optional(),
  transaction_date_from: zDate.optional(),
  transaction_date_to: zDate.optional(),
});

export type TransactionCreateInput = z.infer<typeof TransactionCreateSchema>;
export type TransactionUpdateInput = z.infer<typeof TransactionUpdateSchema>;
export type PaymentStatusReversionInput = z.infer<typeof PaymentStatusReversionSchema>;
export type Transaction = z.infer<typeof TransactionSchema>;
export type TransactionStatusMaster = z.infer<typeof TransactionStatusSchema>;
export type TransactionListQuery = z.infer<typeof TransactionListQuerySchema>;

/**
 * 支払い状況（要件書 2.3・02 5.3.7）：入金日があれば入金済、無くて請求日があれば請求済、どちらも無ければ未請求。
 * サーバーが保存の前に決める。画面は入力中の日付から見込みを出すのに使う。
 */
export function paymentStatusOf(
  invoice_date: string | null | undefined,
  paid_date: string | null | undefined,
): (typeof PAYMENT_STATUS_VALUES)[number] {
  if (paid_date) {
    return 'paid';
  }
  return invoice_date ? 'invoiced' : 'unbilled';
}

/** 支払い状況の順（未請求 → 請求済 → 入金済）。戻す先は今より前だけ（API-TRAN-08） */
export const PAYMENT_STATUS_ORDER: Readonly<Record<(typeof PAYMENT_STATUS_VALUES)[number], number>> = {
  unbilled: 0,
  invoiced: 1,
  paid: 2,
};
